import { defineComponent, h } from 'vue';
import { QIcon, QTooltip } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import {
    tooltipLocationToAnchorSelf,
    tooltipOffsetForPlacement,
    iconAffixClasses,
    ICON_CHROME_BORDER_CLASS,
    normalizeIconAnimation,
    iconTooltipPlacementRaw,
    normalizeIconName,
} from '../../../Shared/buttonAppearance.js';
import '../css/Icon.css';
import DesignerRegistryMixin from './DesignerRegistryMixin.jsx';

const ICON_CLICK_CODE_BEHIND = {
    handlerSuffix: '_Click',
    actionTypeSchema: '/schema/public/Platform.Schema.UIActions.v1/UIAction_Script',
    buildScopeMethodTemplate(methodName) {
        return `\t${methodName}(context) {\n\t\t// Place your click handler here\n\t}`;
    },
};

function literalBool(v) {
    return v === true || v === 'true';
}

export default defineComponent({
    name: 'basic-icon-dsgn',
    mixins: [DesignerRegistryMixin],
    components: { QIcon, QTooltip },
    data: function () {
        return {
        }
    },
    created() {
    },
    computed: {
        Root: function () {
            return this.root;
        },
        selected$: function () {
            return this.Root.SelectedNode && this.Root.SelectedModel == this.designmodel;
        },
        icon() {
            let icon = this.controlData.Icon;

            if (icon && String(icon).includes('{'))
                icon = 'mdi-help-circle-outline';

            return normalizeIconName(icon, { iconStyle: this.controlData.IconStyle }) || 'mdi-help-circle-outline';
        },
        flipHorizontally() {
            return literalBool(this.controlData.FlipHorizontally);
        },
        flipVertically() {
            return literalBool(this.controlData.FlipVertically);
        },
        border() {
            return literalBool(this.controlData.Border);
        },
        animation() {
            return normalizeIconAnimation(this.controlData.Animation);
        },
        tooltip() {
            const t = this.controlData.Tooltip;
            return t == null ? '' : String(t);
        },
        tooltipPlacement() {
            return iconTooltipPlacementRaw(this.controlData);
        },
        iconClasses() {
            return iconAffixClasses({
                flipH: this.flipHorizontally,
                flipV: this.flipVertically,
                animation: this.animation,
            });
        },
        iconStyles: function () {
            const styles = {};

            styles.margin = '15%';

            if (this.controlData.GlowColor)
                styles['text-shadow'] = `0 0 0.2em ${this.controlData.GlowColor}`;

            if (this.controlData.FontSize)
                styles['font-size'] = utils.resolveFontSize(this.controlData.FontSize);

            if (this.controlData.Rotation != null && this.controlData.Rotation !== '')
                styles.transform = `rotate(${this.controlData.Rotation}deg)`;

            if (this.controlData.Color)
                styles.color = this.controlData.Color;

            return styles;
        },
        sizeStyle() {
            return utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent);
        },
        containerStyles() {
            let styles = {
                ...utils.getStyleHints(this.controlData.StyleHints),
                ...this.sizeStyle,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            };
            return styles;
        },
    },
    methods: {
        ...methods,
        handleClick(e) {
            this.Root.SelectNode(this);
            e.cancelBubble = true;
            e.stopPropagation();
        },
        handleDesignerDblClick(e) {
            e.stopPropagation();
            e.preventDefault();
            this.Root?.openCodeBehind?.(this, ICON_CLICK_CODE_BEHIND);
        },
    },
    props: {
        name: '',
        root: null,
        rowData: null,
        designmodel: null,
        parentType: '',
        controlData: {}
    },
    render() {
        let tooltip;
        if (this.tooltip) {
            const { anchor, self } = tooltipLocationToAnchorSelf(this.tooltipPlacement);
            const offset = tooltipOffsetForPlacement(this.tooltipPlacement);
            tooltip = (
                <QTooltip
                    transition-show="scale"
                    transition-hide="scale"
                    anchor={anchor}
                    self={self}
                    {...(offset !== undefined ? { offset } : {})}
                >
                    {utils.renderTooltipContent(this.tooltip)}
                </QTooltip>
            );
        }

        const glyphWrapClass = {
            'c-Icon__glyphWrap': true,
            [ICON_CHROME_BORDER_CLASS]: this.border,
        };

        let icon = (
            <span class={glyphWrapClass}>
                <QIcon name={this.icon} style={this.iconStyles} class={this.iconClasses}>
                    {tooltip}
                </QIcon>
            </span>
        );

        return (
            <div
                style={this.containerStyles}
                class={{ 'c-Icon': true, 'designer-container': true, selected: this.selected$ }}
                onClick={(e) => this.handleClick(e)}
                onDblclick={(e) => this.handleDesignerDblClick(e)}>
                <div class={{ "designer-container-hovering": true, 'designer-container-selected': this.selected$ }}></div>
                {icon}
                <div class="c-icon-caption" style={{ fontSize: this.controlData.CaptionFontSize }}>{this.controlData.Caption }</div>
            </div>
        );
    }
});
